import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { Text } from '@react-three/drei';
import { useWorldStore } from '../state/worldStore';
import { loadTexture } from '../services/textureLoad';
import { ensureMyRecords, onMyRecords, myRecordFor, refreshMyRecords, markVisitBaseline, clearVisitBaseline, generatingRecords } from '../services/myRecords';
import { API_BASE, AUTH_TOKEN } from '../config';
import { audioEngine } from '../services/audioEngine';

// RecordPlaques — the studio's two record surfaces. Same component, two jobs,
// because they are the same object doing different work:
//
//   THE WALL     (source 'released') — behind you. A frame here is EARNED: only
//                a record you actually put out hangs on it, so "each release
//                fills a slot" is literally true. Four frames, and filling them
//                is the milestone the room keeps score of.
//   RESULT CARDS (source 'undecided') — in front of you, at the console. Takes
//                that came back and haven't been put out. INVISIBLE UNTIL
//                NEEDED: an empty card doesn't render, so the board is clear
//                until the room hands you something to decide on.
//
// Clicking either opens artwork + release (RecordPlaquePanel).
//
// PLACED LIKE THE VIDEO CARD, NOT LIKE A BILLBOARD. The promotions/placement
// tool works by raycasting a ghost quad onto world geometry and orienting it to
// the surface normal — which needs geometry. A Marble splat room has none: it is
// a cloud of gaussians with no meshes, no walls, nothing to hit. That is exactly
// why `videoPanel` lives as a hand-tuned transform in rooms.js and is dialled in
// with ?panel=edit, and it is the only method available for hanging anything on
// a splat wall. So these follow it: authored in rooms.js, tuned in-world.
//
// The two surfaces treat EMPTY differently, and that difference is the design.
// An empty frame on the wall is the argument for making the next record, so it
// renders and says "next one". An empty result card is just clutter on a desk,
// so it renders as nothing at all — the studio only puts a record in front of
// you when there is a record to put there.

// One printer for both the console log and the panel's copy button, so they can
// never disagree. Zero angles are omitted, so a wall frame doesn't carry
// rotX/rotZ noise it never uses.
export function line(v, r) {
  const parts = [`pos: [${v.pos.map(r).join(', ')}]`, `rotY: ${r(v.rotY || 0)}`];
  if (v.rotX) parts.push(`rotX: ${r(v.rotX)}`);
  if (v.rotZ) parts.push(`rotZ: ${r(v.rotZ)}`);
  parts.push(`size: ${r(v.size)}`);
  return `{ ${parts.join(', ')} }`;
}

export default function RecordPlaques({
  plaques, storageKey = 'plaques:studio', source = 'released', group = 'wall',
}) {
  const myAvatarId = useWorldStore((s) => s.myAvatarId);
  const setActivePlaque = useWorldStore((s) => s.setActivePlaque);
  const activePlaque = useWorldStore((s) => s.activePlaque);
  // Driven by the ⚙ hub, not a URL param. Every other tuning tool lives there,
  // and a placement editor you can only reach by knowing a query string is a
  // tool nobody finds twice.
  const editMode = useWorldStore((s) => s.adminTool) === 'plaques';
  const setPlaqueEdit = useWorldStore((s) => s.setPlaqueEdit);
  const edit = useWorldStore((s) => s.plaqueEdit);
  const mine = edit.group === group;

  // Tuned placement survives reloads (same contract as videoPanel): edits
  // auto-save per room and win over rooms.js until 'r' resets them, so a
  // placement session can't be lost to a refresh.
  const loadSaved = () => {
    try { return JSON.parse(localStorage.getItem(storageKey) || 'null'); } catch { return null; }
  };
  const [xf, setXf] = useState(() => loadSaved() || plaques);
  const sel = mine ? edit.i : -1;
  const { camera } = useThree();

  useEffect(() => {
    const saved = loadSaved();
    if (saved?.length) console.log(`[plaque] restored saved placement (${storageKey})`);
    setXf(saved?.length ? saved : plaques);
  }, [plaques, storageKey]);

  const setPlaqueXf = useWorldStore((s) => s.setPlaqueXf);
  useEffect(() => { setPlaqueXf(group, xf); }, [xf, group, setPlaqueXf]);
  // The panel's nudge buttons write here; the wall re-reads on the next render.
  const nudge = useWorldStore((s) => s.plaqueNudge);
  useEffect(() => {
    if (!nudge || nudge.group !== group) return;
    setXf((list) => {
      const out = list.map((v, i) => (i === nudge.i ? { ...v, ...nudge.patch } : v));
      localStorage.setItem(storageKey, JSON.stringify(out));
      return out;
    });
  }, [nudge]); // eslint-disable-line react-hooks/exhaustive-deps

  const [tick, setTick] = useState(0);
  // Which card is currently squared-up to the camera. Owned here rather than
  // per-card so facing one card un-faces its neighbour.
  const [facedSlot, setFacedSlot] = useState(null);
  useEffect(() => {
    if (!myAvatarId) return undefined;
    ensureMyRecords(myAvatarId).then(() => {
      // Snapshot AFTER the first load, and only for the result cards: anything
      // that shows up from here on is this visit's work. Walking out clears it,
      // so the next visit starts from a clean desk.
      if (source !== 'released') markVisitBaseline();
      setTick((n) => n + 1);
    });
    const off = onMyRecords(() => setTick((n) => n + 1));
    // A TAKE THAT LANDS SHOULD LAND ON THE BOARD (Khari, 2026-08-03: "the play
    // button does not work until the second track arrives"). The card for a
    // still-generating take was fetched with no audio and no variant role, and
    // nothing ever re-read it — so it sat there looking finished and broken
    // while its sibling came back complete. Poll only while something is
    // actually cooking, and stop the moment the desk is settled.
    const poll = setInterval(() => {
      if (generatingRecords().length) refreshMyRecords();
    }, 10000);
    return () => {
      off();
      clearInterval(poll);
      if (source !== 'released') clearVisitBaseline();
    };
  }, [myAvatarId, source]);

  // Same keys as ?panel=edit, plus 1-9 to pick which frame you're moving.
  useEffect(() => {
    if (!editMode) return undefined;
    const onKey = (e) => {
      if (!mine) return;
      if (e.key >= '1' && e.key <= '9') { setPlaqueEdit({ group, i: Math.min(Number(e.key) - 1, xf.length - 1) }); return; }
      if (e.key === 'r') {
        localStorage.removeItem(storageKey);
        setXf(plaques);
        console.log('[plaque] reset to rooms.js defaults');
        return;
      }
      setXf((list) => {
        const s = list[sel];
        if (!s) return list;
        const [x, y, z] = s.pos;
        // Camera-relative ground axes, so arrows move the frame the way it looks
        // like they should from wherever you're standing.
        let fx = 0, fz = -1, rx = 1, rz = 0;
        const dir = new THREE.Vector3();
        camera.getWorldDirection(dir);
        dir.y = 0;
        if (dir.lengthSq() > 1e-6) {
          dir.normalize();
          fx = dir.x; fz = dir.z;
          rx = -dir.z; rz = dir.x;
        }
        const dist = Math.max(1, camera.position.distanceTo(new THREE.Vector3(x, y, z)));
        const step = (e.shiftKey ? 0.008 : 0.04) * dist;
        let next = s;
        if (e.key === 'ArrowLeft') next = { ...s, pos: [x - rx * step, y, z - rz * step] };
        else if (e.key === 'ArrowRight') next = { ...s, pos: [x + rx * step, y, z + rz * step] };
        else if (e.key === 'ArrowUp') next = { ...s, pos: [x + fx * step, y, z + fz * step] };
        else if (e.key === 'ArrowDown') next = { ...s, pos: [x - fx * step, y, z - fz * step] };
        else if (e.key === 'PageUp' || e.key === 'u') next = { ...s, pos: [x, y + step * 0.6, z] };
        else if (e.key === 'PageDown' || e.key === 'j') next = { ...s, pos: [x, y - step * 0.6, z] };
        else if (e.key === '[') next = { ...s, rotY: (s.rotY || 0) + 0.08 };
        else if (e.key === ']') next = { ...s, rotY: (s.rotY || 0) - 0.08 };
        // Roll (Z) and tilt (X) — what a card laid flat on the console needs.
        else if (e.key === ',') next = { ...s, rotZ: (s.rotZ || 0) + 0.08 };
        else if (e.key === '.') next = { ...s, rotZ: (s.rotZ || 0) - 0.08 };
        else if (e.key === ';') next = { ...s, rotX: (s.rotX || 0) + 0.08 };
        else if (e.key === "'") next = { ...s, rotX: (s.rotX || 0) - 0.08 };
        else if (e.key === '-') next = { ...s, size: Math.max(0.1, s.size - 0.02) };
        else if (e.key === '=') next = { ...s, size: s.size + 0.02 };
        else return list;
        e.preventDefault();
        const out = list.map((v, i) => (i === sel ? next : v));
        localStorage.setItem(storageKey, JSON.stringify(out));
        const r = (v) => Math.round(v * 100) / 100;
        console.log(`${group === 'wall' ? 'plaques' : 'resultCards'}: [\n` + out.map((v) =>
          `      ${line(v, r)},`).join('\n') + '\n    ],');
        return out;
      });
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [editMode, mine, sel, xf.length, camera, plaques, storageKey, group, setPlaqueEdit]);

  return (
    <group>
      {xf.map((f, i) => (
        <Plaque
          key={i}
          xf={f}
          slot={i}
          tick={tick}
          source={source}
          faceOnHover={source !== 'released'}
          hideWhenEmpty={source !== 'released'}
          editing={editMode}
          selected={editMode && sel === i}
          active={activePlaque?.group === group && activePlaque?.i === i}
          onOpen={() => setActivePlaque({ group, i, source })}
          // ONE CARD FACES AT A TIME (Khari, 2026-08-03: "the buttons don't
          // seem to work in general.. only the second one is playing").
          // Facing more than doubles a card's invisible hover-catcher — s to
          // s*2.05 wide — so two neighbours facing together overlap, and the
          // catcher nearest the camera swallows every click meant for the
          // other one. Which is exactly what it looked like: two cards
          // stacked, one card answering for both.
          facedSlot={facedSlot}
          setFacedSlot={setFacedSlot}
        />
      ))}
    </group>
  );
}

function Plaque({ xf, slot, tick, source, faceOnHover, hideWhenEmpty, editing, selected, active, onOpen, facedSlot, setFacedSlot }) {
  const [tex, setTex] = useState(null);
  const ref = useRef();
  const pivot = useRef();
  const { camera } = useThree();
  // Hover is now group-scoped: the last card pointed at is the faced one, and
  // it is the ONLY one, so no two catchers are ever expanded over each other.
  const hover = facedSlot === slot;
  const setHover = (on) => setFacedSlot((cur) => (on ? slot : (cur === slot ? null : cur)));
  const faced = faceOnHover && (hover || active) && !editing;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const track = useMemo(() => myRecordFor(source, slot), [slot, tick, source]);
  const url = track?.artwork_url || null;
  const out = !!track && (track.status === 'released' || track.is_released);

  useEffect(() => {
    if (!url) { setTex(null); return undefined; }
    let dead = false;
    loadTexture(url).then((t) => { if (!dead && t) setTex(t); });
    return () => { dead = true; };
  }, [url]);

  // A result card LIES WHERE IT WAS PUT — flat on the console, at whatever
  // angle it was dropped — and squares up to you when you look at it. That is
  // the whole trick: it reads as an object in the room until you need it to be
  // a control, and then it becomes one without you having to open anything.
  //
  // The lerp is on a PIVOT inside the placed transform, so the authored
  // rotation stays the resting pose and this only ever borrows it.
  const q = useMemo(() => ({ target: new THREE.Quaternion(), inv: new THREE.Quaternion() }), []);
  useFrame(() => {
    if (ref.current) {
      const t = hover || active ? 1.06 : 1;
      ref.current.scale.x += (t - ref.current.scale.x) * 0.2;
      ref.current.scale.y = ref.current.scale.x;
    }
    if (!pivot.current) return;
    if (faced) {
      // World-space camera orientation, converted into the parent's frame so it
      // cancels whatever roll/tilt the card was placed at.
      pivot.current.parent.getWorldQuaternion(q.inv);
      q.inv.invert();
      q.target.copy(camera.quaternion).premultiply(q.inv);
    } else {
      q.target.identity();
    }
    pivot.current.quaternion.slerp(q.target, 0.16);
  });

  // Nothing to decide → nothing on the desk. Held open in EDIT mode, or the
  // frames would vanish exactly when you are trying to position them.
  if (hideWhenEmpty && !track && !editing) return null;

  const s = xf.size;
  return (
    <group position={xf.pos} rotation={[xf.rotX || 0, xf.rotY || 0, xf.rotZ || 0]}>
     <group ref={pivot}>
      <group ref={ref}>
        {/* HOVER LIVES ON A BIGGER, INVISIBLE PLANE — not on the artwork.
            Tracking it on the art meant that moving toward the buttons left the
            art, hover dropped, the card un-faced, and the buttons went away
            before you could reach them. The catcher spans the card AND the
            title and controls that appear around it, so the whole faced object
            is one hover target. It sits BEHIND everything, so the buttons in
            front still receive their own clicks. */}
        <mesh position={[0, faced ? -s * 0.06 : 0, -0.004]} visible={false}
          onPointerOver={(e) => { e.stopPropagation(); setHover(true); document.body.style.cursor = 'pointer'; }}
          onPointerOut={() => { setHover(false); document.body.style.cursor = 'auto'; }}
        >
          <planeGeometry args={faced ? [s * 2.05, s * 1.85] : [s, s]} />
        </mesh>
        <mesh
          onClick={(e) => { e.stopPropagation(); onOpen(); }}
        >
          <planeGeometry args={[s, s]} />
          {/* KEYED on the texture: assigning `map` to an already-compiled
              material leaves it without USE_MAP and resets colour to white —
              the "white box until you reopen it" bug from the ad placements. */}
          {/* Unreleased sits back in the frame: dimmed and cooled, so a wall of
              four reads at a glance as "three out, one still to go". */}
          <meshBasicMaterial
            key={tex ? tex.uuid : 'blank'}
            map={tex || null}
            color={tex ? '#ffffff' : '#140f18'}
            transparent
            opacity={tex ? 1 : 0.55}
            toneMapped={false}
          />
        </mesh>

        {/* Artwork is generated separately, so a fresh take often has none yet.
            Without this it renders as an anonymous shaded square — name it, and
            it reads as a record waiting for a cover. */}
        {track && !tex && (
          <Text position={[0, 0, 0.004]} fontSize={s * 0.09} color="#ffe9c4" anchorX="center" anchorY="middle" maxWidth={s * 0.82} textAlign="center" toneMapped={false}>
            {track.title || 'Untitled'}
          </Text>
        )}

        {/* An empty frame says what it is, so it reads as waiting rather than broken. */}
        {!track && (
          <Text position={[0, 0, 0.004]} fontSize={s * 0.1} color="#ffb25e" anchorX="center" anchorY="middle" maxWidth={s * 0.8} textAlign="center" toneMapped={false}>
            next one
          </Text>
        )}

        {(hover || active || selected) && (
          <lineSegments position={[0, 0, 0.005]}>
            <edgesGeometry args={[new THREE.PlaneGeometry(s * 1.1, s * 1.1)]} />
            <lineBasicMaterial color={selected ? '#7dff9d' : '#ffb25e'} />
          </lineSegments>
        )}
      </group>

      {/* Faced = reviewing it. Title above, the four things you can do below —
          on the card itself, so the decision happens where the record is. */}
      {faced && track && (
        <CardFace track={track} s={s} out={out} keepFaced={setHover} />
      )}
     </group>

      {editing && (
        <Text position={[0, s * 0.72, 0.01]} fontSize={s * 0.14} color={selected ? '#7dff9d' : '#ffd27d'} anchorX="center" anchorY="middle" toneMapped={false}>
          {slot + 1}
        </Text>
      )}
    </group>
  );
}

// ── The faced card ──────────────────────────────────────────────────────────
// Title, and the four things you can do to a take that just came back. In the
// card rather than in a side panel, because the decision belongs where the
// record is — and because a panel would put the thing you're judging behind it.
//
// Reroll and archive are the two that were missing everywhere else: without
// them a bad take is permanent and the only options are "release it" or "leave
// it sitting there", which is not how anyone actually works.
// TWO TAKES, ONE RECORD. The generator always returns two, so the pair on the
// board is a CHOICE before it is anything else — and until it's made, the other
// actions are noise. "This one" resolves the run (winner + archive the sibling)
// and only then does the record become something you can art up and put out.
const CHOOSE = { key: 'choose', label: 'This one', path: 'choose', method: 'POST' };
const ACTIONS = [
  { key: 'art', label: 'Artwork', path: 'generate-artwork', method: 'POST' },
  { key: 'release', label: 'Put it out', path: 'publish', method: 'POST', hideWhenOut: true },
  { key: 'reroll', label: 'Reroll', path: 'reroll', method: 'POST', hideWhenOut: true },
  { key: 'archive', label: 'Archive', path: 'archive', method: 'PUT', body: { archived: true } },
];

// `keepFaced` — every interactive part of the card re-asserts hover.
//
// Khari, 2026-08-04: "the hit area is in the square.. the buttons needed are at
// the bottom. they cannot be touched because the object moves back into view
// when you stop hovering over the center of the square."
//
// The catcher plane behind the card is big enough to cover the buttons, but the
// BUTTONS SIT IN FRONT OF IT — so the moment the pointer crossed onto one, the
// catcher received pointerout, the card un-faced, and the control moved out from
// under the cursor. A card you cannot reach the controls of. Each control now
// says "still here" on pointerover, so approaching a button keeps the card
// squared up instead of dismissing it.
function CardFace({ track, s, out, keepFaced }) {
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');
  const id = track.track_id || track.id;

  const run = async (a) => {
    if (busy) return;
    setBusy(a.key); setMsg('');
    try {
      // RELEASING IS AN EVENT, NOT A ROW (Khari, 2026-08-03: "it showed up on
      // the wall and nothing else happened.. no trigger, no congrats").
      // /publish only writes the record; the premiere — rotation, and the first
      // play happening out on the street — lived only inside the producer
      // scene's release tool, so putting a record out anywhere else in the
      // world silently skipped the payoff (§4.6).
      const url = a.key === 'release'
        ? `${API_BASE}/api/world/premiere/${id}`
        : `${API_BASE}/api/tracks/${id}/${a.path}`;
      const res = await fetch(url, {
        method: a.method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${AUTH_TOKEN}` },
        body: JSON.stringify(a.body || {}),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || a.label + ' failed');
      await refreshMyRecords();
      if (a.key === 'release' && data.premiere) {
        // Arms PremiereMoment — it fires the instant they step outside.
        useWorldStore.getState().setPremiere(data.premiere);
        setMsg('it\u2019s out — go outside');
        // THE WALL CATCHES UP (Khari, 2026-08-03: "my album did not show on the
        // wall until I refreshed the session"). Publishing flips status through
        // a workflow, so the refresh fired above can read the row a beat before
        // it says 'released' — and the wall only draws released records. Read
        // it again shortly after, so the frame fills while they are still
        // standing there instead of on their next visit.
        setTimeout(() => { refreshMyRecords(); }, 2500);
        setTimeout(() => { refreshMyRecords(); }, 7000);
        return;
      }
      setMsg(a.key === 'reroll' ? 'running it back…' : a.key === 'choose' ? 'that\u2019s the one' : 'done');
    } catch (err) {
      setMsg(String(err.message || '').slice(0, 44));
    } finally { setBusy(''); }
  };

  // An unresolved candidate gets ONE button. Offering "put it out" next to
  // "this one" would let you release a take you hadn't chosen, which is how you
  // end up with both halves of a run in your catalogue.
  const undecided = track.variant_role === 'candidate';
  // A take with no audio yet cannot be played, chosen, arted, or released —
  // only archived. Showing the full row makes every one of them look broken.
  const playable = !!track.audio_url;
  const shown = !playable ? [ACTIONS[3]]
    : undecided ? [CHOOSE, ACTIONS[3]]
    : ACTIONS.filter((a) => !(a.hideWhenOut && out));
  const bw = (s * 1.9) / shown.length;
  const by = -s * 0.62;

  return (
    <group position={[0, 0, 0.01]}>
      <Text position={[0, s * 0.62, 0]} fontSize={s * 0.11} color="#ffe9c4" anchorX="center" anchorY="middle" maxWidth={s * 1.8} textAlign="center" toneMapped={false}>
        {track.title || 'Untitled'}
      </Text>
      <Text position={[0, s * 0.5, 0]} fontSize={s * 0.07} color={out ? '#9dff7d' : '#ffd27d'} anchorX="center" anchorY="middle" toneMapped={false}>
        {busy ? '…' : (msg || (!playable ? 'still rendering…' : out ? 'released' : undecided ? 'take — pick one' : 'not out yet'))}
      </Text>

      {/* Play sits ON the art — the first thing you want is to hear it. But a
          take that is still rendering has no audio yet, and a play button that
          answers a click with nothing reads as broken (Khari, 2026-08-03: "the
          play button does not work until the second track arrives"). No audio,
          no button — the line above says why. */}
      {playable && (
      <group
        position={[0, 0, 0.002]}
        onClick={(e) => { e.stopPropagation(); audioEngine.playTrack(track); }}
        onPointerOver={(e) => { e.stopPropagation(); keepFaced?.(true); document.body.style.cursor = 'pointer'; }}
      >
        <mesh>
          <circleGeometry args={[s * 0.16, 28]} />
          <meshBasicMaterial color="#0d0a10" transparent opacity={0.72} toneMapped={false} />
        </mesh>
        <Text position={[s * 0.02, 0, 0.001]} fontSize={s * 0.15} color="#ffe9c4" anchorX="center" anchorY="middle" toneMapped={false}>▶</Text>
      </group>
      )}

      {shown.map((a, i) => {
        const x = -((shown.length - 1) / 2) * bw + i * bw;
        return (
          <group
            key={a.key}
            position={[x, by, 0]}
            onClick={(e) => { e.stopPropagation(); run(a); }}
            onPointerOver={(e) => { e.stopPropagation(); keepFaced?.(true); document.body.style.cursor = 'pointer'; }}
          >
            <mesh>
              <planeGeometry args={[bw * 0.92, s * 0.19]} />
              <meshBasicMaterial color={busy === a.key ? '#3a2a16' : '#1a1218'} transparent opacity={0.94} toneMapped={false} />
            </mesh>
            <Text position={[0, 0, 0.002]} fontSize={s * 0.075} color="#ffd27d" anchorX="center" anchorY="middle" maxWidth={bw * 0.85} toneMapped={false}>
              {a.label}
            </Text>
          </group>
        );
      })}
    </group>
  );
}
